<template>
  <el-dropdown
    trigger="click"
    popper-class="risk-level-menu"
    placement="bottom-start"
    :disabled="!canEditRiskLevel"
    @command="onChange">
    <span class="risk-btn">
      <i
        class="risk-icon mr-2"
        :class="riskLevel ? `risk-icon-${riskLevel}` : 'hidden'"/>
      {{ $t(`entity.risk_level.${riskLevel || 'no'}`) }}
      <SvgIcon
        name="icon-chevron-down"
        type="sized"
        size="16"/>
    </span>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="level in ONBOARDING_RISK_LEVELS"
          :key="level"
          :command="level">
          <i
            class="risk-icon mr-2"
            :class="`risk-icon-${level}`"/>
          {{ $t(`entity.risk_level.${level}`) }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang='ts'>
import { computed, ref } from 'vue';

import SvgIcon from '@/_redesign/shared/ui/SvgIcon/SvgIcon.vue';
import { ONBOARDING_RISK_LEVELS } from '@/constants/onboarding';
import { requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';

const { riskLevel, applicationStatus, changeHandler } = defineProps<{
  riskLevel: Onboarding.RiskLevel | undefined;
  applicationStatus: Onboarding.ApplicationStatus;
  changeHandler: Function;
}>();
const isLoading = ref(false);

async function onChange(newStatus: Onboarding.RiskLevel) {
  await requestHandler(() => changeHandler(newStatus), {
    pending: isLoading,
  });
}

const canEditRiskLevel = computed(() => {
  const validStatuses: Onboarding.ApplicationStatus[] = ['NON_VERIFIED', 'MANUAL', 'VERIFIED', 'IN_PROGRESS'];
  return validStatuses.includes(applicationStatus) && !isLoading.value;
});
</script>

<style lang='scss'>
.risk-level {
  &-menu {
    @apply rounded-12 shadow-dropdown border-2 border-old-gray-10 #{!important};

    .dropdown-title {
      @apply font-bold text-base;
    }

    .el-dropdown-menu {
      @apply px-12 py-16 flex gap-8 flex-col w-[240px] font-main rounded-12;

      &__item {
        @apply px-10 py-6 rounded-6;
        @apply text-neutral-primary-default hover:text-neutral-primary-default focus:text-neutral-primary-default font-medium text-base;
        @apply hover:bg-old-gray-10 focus:bg-old-gray-10;

        span {
          @apply flex items-center text-base font-medium;
        }
      }
    }

    .el-popper__arrow {
      display: none;
    }

    i::before {
      @apply text-lg;
    }

    .sections {
      @apply px-12 py-8 bg-old-red-10 rounded-8 text-sm leading-[18px];
    }
  }
}

.risk-icon {
  @apply w-16 h-16 bg-no-repeat bg-center;

  &-LOW {
    @apply bg-[url("@/assets/icons/risk-low.svg")];
  }

  &-MEDIUM {
    @apply bg-[url("@/assets/icons/risk-medium.svg")];
  }

  &-HIGH {
    @apply bg-[url("@/assets/icons/risk-high.svg")];
  }
}

.risk-btn {
  @apply flex items-center gap-2;
  @apply px-12 py-6;
  @apply hover:rounded-6 hover:bg-old-gray-10;
}

</style>
